/**
 * CategoryGroupSelect · 二级分组多选下拉
 *
 * 实现策略：薄壳复用 `NewRegionSelect`，避免重复造轮子。
 *
 * 数据契约（对外）：
 * - options: Array<{ value, text, textCn, children: [{ value, text, textCn }] }>
 *   由 condDealwith 把 cond 接口的 { value, label_en, label_zh, children } 结构转换得到
 * - value:   Array<{ l1: string; l2: string[] }>
 *   每个 L1 携带其被选中的 L2 子集；某个 L1 完全未选中时不在数组中
 *
 * 适配层（内部）：
 * - 字段名：`textCn` ↔ `textCN`（NewRegionSelect 用首大写）
 * - L2 值：`prefix-encode` 为 `${l1}\u0001${l2}`，保证扁平 markets 数组中全局唯一
 *   （NewRegionSelect 把 L2 拍平到 `markets: string[]`，假设 L2 value 全局唯一，
 *    Category L2 不保证唯一，所以加 L1 前缀避免冲突；输出时再解码还原）
 * - value：`Array<{ l1, l2[] }>` ↔ `{ type: 'market', regions: [], markets: [encodedL2...] }`
 *   `regions` 由 NewRegionSelect 内部根据 markets 自动派生，不需要外部维护
 */
import React, { useCallback, useMemo } from 'react';
import { connect } from 'umi';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import useIntls from '@opm/hooks/useIntls';
import { DropdownProps } from 'tea-component';
import type { IMultipleCommonType } from '../../types';
import {
  multipleMapStateToProps,
  moreKeyMultipleDispatchToProps,
} from '../../connect/multiple';
import { CategoryGroupSelectButton } from './CategoryGroupSelectButton';
import './index.scss';

export interface ICategoryGroupChild {
  value: string;
  text: string;
  textCn: string;
}

export interface ICategoryGroupOption {
  value: string;
  text: string;
  textCn: string;
  children?: ICategoryGroupChild[];
}

export interface ICategoryGroupValue {
  l1: string;
  l2: string[];
}

export type TCategoryGroupSelectType = {
  options: ICategoryGroupOption[];
  valueKeys: string;
  title: string;
  titleCn: string;
  /** 按钮上最多平铺展示几个具体 L2 名称，默认 2 */
  maxShow?: number;
} & Omit<DropdownProps, 'button' | 'children' | 'options' | 'value'>;

interface IInnerProps
  extends TCategoryGroupSelectType,
  Omit<IMultipleCommonType, 'value' | 'change'> {
  value: ICategoryGroupValue[];
  change: (next: ICategoryGroupValue[], isUpdate?: boolean) => void;
  /**
   * 由 moreKeyMultipleDispatchToProps 注入：关闭下拉时派发 UPDATE:true，
   * 触发上层 useUpdateEffect → handleConfirm → 把当前 redux 值通过 backValue 推给外层 filter
   */
  onClose?: () => void;
}

/** 编码分隔符：Unit Separator（\u0001），保证不会出现在业务 ID 中 */
const ENCODE_SEP = '\u0001';

function encodeL2(l1: string, l2: string): string {
  return `${l1}${ENCODE_SEP}${l2}`;
}

function decodeL2(encoded: string): { l1: string; l2: string; } {
  const idx = encoded.indexOf(ENCODE_SEP);
  if (idx < 0) return { l1: '', l2: encoded };
  return { l1: encoded.slice(0, idx), l2: encoded.slice(idx + 1) };
}

function CategoryGroupSelectInner({
  options = [],
  value = [],
  change,
  onClose,
  title,
  titleCn,
  maxShow = 2,
}: IInnerProps) {
  const { isEn } = useIntls();
  const locale = isEn ? 'en-US' : 'zh-CN';

  /** options 适配：textCn → textCN，L2 value 加 L1 前缀避免扁平冲突 */
  const adaptedOptions = useMemo(
    () =>
      options.map((opt) => ({
        value: opt.value,
        text: opt.text,
        textCN: opt.textCn,
        children: (opt.children || []).map((c) => ({
          value: encodeL2(opt.value, c.value),
          text: c.text,
          textCN: c.textCn,
        })),
      })),
    [options],
  );

  /** value 适配：`Array<{l1, l2[]}>` → `{ type, regions, markets }` */
  const adaptedValue = useMemo(() => {
    const markets: string[] = [];
    (value || []).forEach((it) => {
      (it.l2 || []).forEach((l2) => markets.push(encodeL2(it.l1, l2)));
    });
    return { type: 'market', regions: [], markets };
  }, [value]);

  /** onChange 适配：`{ type, regions, markets }` → `Array<{l1, l2[]}>` */
  const handleChange = useCallback(
    (next: any) => {
      if (!next) {
        change([], false);
        return;
      }
      const grouped: Record<string, string[]> = {};
      (next.markets || []).forEach((m: string) => {
        const { l1, l2 } = decodeL2(m);
        if (!l1) return;
        if (!grouped[l1]) grouped[l1] = [];
        grouped[l1].push(l2);
      });
      const result: ICategoryGroupValue[] = Object.keys(grouped).map((l1) => ({
        l1,
        l2: grouped[l1],
      }));
      change(result, false);
    },
    [change],
  );

  const buttonElement = (
    <CategoryGroupSelectButton
      value={value}
      options={options}
      title={title}
      titleCn={titleCn}
      maxShow={maxShow}
    />
  );

  /** L1 列标题（覆盖 NewRegionSelect 默认的 "Region" / "区域"） */
  const firstLevelTitle = isEn ? title : titleCn;

  /**
   * NewRegionSelect 的 RegionSelectPropsType 把若干"内部派生值"（如 realRegionsList）
   * 错误地声明为必填 prop，这里用 any 透传所有 props，避免污染调用方
   *
   * `onCloseReginSelect` 必须接上 `onClose`：下拉关闭时派发 UPDATE:true，
   * 否则 FilterCommon 的 useUpdateEffect 不会触发 handleConfirm，list 接口拿不到新值。
   */
  const newRegionSelectProps: any = {
    value: adaptedValue,
    options: adaptedOptions,
    button: buttonElement,
    locale,
    firstLevelTitle,
    radioOptions: [],
    shortcutOptions: [],
    popularList: [],
    isNotRegionIcon: true,
    closeOnScroll: true,
    onChange: handleChange,
    onCloseReginSelect: () => {
      onClose?.();
    },
  };

  return (
    <div className="dbom-category-group-select-wrapper">
      <NewRegionSelect {...newRegionSelectProps} />
    </div>
  );
}

export default connect(
  multipleMapStateToProps,
  moreKeyMultipleDispatchToProps,
)(CategoryGroupSelectInner as any);
